import { Checkbox, Chip, Grid, IconButton, MenuItem, Select, TextField } from "@mui/material";
import { RuleData } from "./RuleEditor";
import { useCallback, useState } from "react";
import draggable from "../../../components/content/icons/draggable.svg";
import menu from "../../../components/content/icons/menu.svg";
import { RuleMenu } from "./RuleMenu";
import { Aligned } from "components/Layout";

interface IProps {
  ruleData: RuleData;
  onModify: (rule: RuleData) => any;
  addRule: (rule: RuleData, position: number) => any;
  deleteRule: (rule: RuleData) => any;
}

export const Rule = ({ ruleData, onModify, addRule, deleteRule }: IProps) => {
  // we use local state for each rule as it is more performant than an onchange
  // of a rule in a large list. The onBlur for the textfield pushes the text over
  // to the master list.
  const [source, setSource] = useState(ruleData.sourceMatchScript);
  const [target, setTarget] = useState(ruleData.targetMatchScript);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const onRuleChange = useCallback(
    (e: any, key: string) => {
      const newRuleData = { ...ruleData, [key]: key === "checked" ? !ruleData.checked : e.target.value };
      onModify(newRuleData);
    },
    [onModify, ruleData],
  );
  const onAddRule = (position: number) => {
    addRule(ruleData, position);
    handleCloseRuleMenu();
  };
  const onDeleteRule = () => {
    deleteRule(ruleData);
    handleCloseRuleMenu();
  };

  const onRuleMenuClicked = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleCloseRuleMenu = () => setAnchorEl(null);

  return (
    <>
      <Grid item xs={1} style={{ display: "flex", justifyContent: "center", alignItems: "right" }}>
        <Aligned>
          <img src={draggable} alt="drag" height="18px" />
          <Checkbox
            id="checked"
            size="small"
            style={{ color: "#414141" }}
            onChange={(e) => onRuleChange(e, "checked")}
            checked={ruleData.checked}
          />
        </Aligned>
      </Grid>
      <Grid item xs={1} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Select
          labelId="type"
          id="type"
          value={ruleData.type}
          label=""
          IconComponent={() => null}
          onChange={(e) => onRuleChange(e, "type")}
          sx={{ border: "0px solid" }}
          displayEmpty
        >
          <MenuItem value="">
            <Chip label="Select" style={{ backgroundColor: "transparent", color: "#999999", fontStyle: "italic" }} />
          </MenuItem>
          <MenuItem value="Section">
            <Chip label="Section" style={{ backgroundColor: "#EEEEEE", color: "#252525" }} />
          </MenuItem>
          <MenuItem value="Module">
            <Chip label="Module" style={{ backgroundColor: "#FAFAFA", color: "#34227A" }} />
          </MenuItem>
        </Select>
      </Grid>
      {/*
      <Grid item xs={1} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Select
          labelId="rule"
          id="rule"
          value={ruleData.rule}
          label=""
          IconComponent={() => null}
          onChange={(e) => onRuleChange(e, "rule")}
          sx={{ border: "0px solid" }}
          displayEmpty
        >
          <MenuItem value="">
            <Chip label="Select" style={{ backgroundColor: "transparent", color: "#999999", fontStyle: "italic" }} />
          </MenuItem>
          <MenuItem value="Custom">
            <Chip label="Custom" style={{ backgroundColor: "#FBE6F1", color: "#98004E" }} />
          </MenuItem>
          <MenuItem value="Contains">
            <Chip label="Contains" style={{ backgroundColor: "#EEEEEE", color: "#252525" }} />
          </MenuItem>
          <MenuItem value="Equals">
            <Chip label="Equals" style={{ backgroundColor: "#F1EEE8", color: "#503E14" }} />
          </MenuItem>
        </Select>
      </Grid>*/}
      <Grid item xs={6} style={{ display: "flex", justifyContent: "left", alignItems: "left" }}>
        <TextField
          multiline
          fullWidth
          placeholder="Add value"
          id="source"
          variant="standard"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          onBlur={(e) => onRuleChange(e, "sourceMatchScript")}
          inputProps={{
            style: {
              paddingLeft: "5px",
              paddingRight: "5px",
              fontFamily: "Roboto,sans-serif",
              fontSize: "13px",
              fontWeight: "400",
              lineHeight: "24px",
              color: "#252525",
            },
          }}
        />
      </Grid>
      <Grid item xs={1} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Select
          labelId="action"
          id="action"
          value={ruleData.action}
          label=""
          sx={{ border: "0px solid" }}
          IconComponent={() => null}
          onChange={(e) => onRuleChange(e, "action")}
          displayEmpty
        >
          <MenuItem value="">
            <Chip label="Select" style={{ backgroundColor: "transparent", color: "#999999", fontStyle: "italic" }} />
          </MenuItem>
          <MenuItem value="Move">
            <Chip label="Move" style={{ backgroundColor: "#DFF9D2", color: "#398712" }} />
          </MenuItem>
          <MenuItem value="Exclude">
            <Chip label="Exclude" style={{ backgroundColor: "#FFF1E5", color: "#663100" }} />
          </MenuItem>
          <MenuItem value="Delete">
            <Chip label="Delete" style={{ backgroundColor: "#FEF1F2", color: "#881113" }} />
          </MenuItem>
        </Select>
      </Grid>
      <Grid item xs={2} style={{ display: "flex", justifyContent: "left", alignItems: "left" }}>
        <TextField
          id="target"
          variant="standard"
          placeholder="Add value"
          fullWidth
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          onBlur={(e) => onRuleChange(e, "targetMatchScript")}
          inputProps={{
            style: {
              fontFamily: "Roboto,sans-serif",
              paddingLeft: "5px",
              paddingRight: "5px",
              fontSize: "13px",
              fontWeight: "400",
              lineHeight: "24px",
              color: "#252525",
            },
          }}
        />
      </Grid>
      <Grid item xs={1} style={{ display: "flex", justifyContent: "right", alignItems: "right" }}>
        <IconButton aria-owns={anchorEl ? "rule-menu" : undefined} aria-haspopup="true" onClick={onRuleMenuClicked}>
          <img src={menu} alt="menu" />
        </IconButton>
      </Grid>
      <RuleMenu anchor={anchorEl} onClose={handleCloseRuleMenu} onAddRule={onAddRule} onDeleteRule={onDeleteRule} />
    </>
  );
};
